import React from 'react';
import { useRecoilState } from 'recoil';
import { useEffect, useState } from 'react';
import { getAvailabilities } from '../services/fosService';
import { FOSTable } from '../common/table/FOSTable';
import { FOSTableTooltipCell } from '../common/table/FOSTableTooltipCell';
import { FOSTableDateCell } from '../common/table/FOSTableDateCell';
import { FOSTableVesselLinkCell } from '../common/table/FOSTableVesselLinkCell';
import { FOSTableAvailabilityDateCell } from '../common/table/FOSTableAvailabilityDateCell';
import { FOSTableCurrentChartererCell } from '../common/table/FOSTableCurrentChartererCell';
import { FOSTableNextChartererCell } from '../common/table/FOSTableNextChartererCell';
import { availabilityDateChecker } from '../util';
import { NumericFormat } from 'react-number-format';
import { modalEditorOpen } from '../state/modals';

enum Tab {
  AHTS = 'ahts',
  PSV = 'psv',
  Standby = 'standby',
}

export const AvailabilityNorway = (props: any) => {
  const columns_other = React.useMemo(
    () => [
      {
        Header: 'Vessel Name',
        accessor: 'vessel_name',
        Cell: ({ row }: any) => (
          <FOSTableVesselLinkCell
            imo={row.original.imo}
            vesselName={row.original.vessel_name}
          ></FOSTableVesselLinkCell>
        ),
      },
      {
        Header: 'BHP',
        accessor: 'bhp',
        Cell: ({ value }: any) => (
          <span>
            <NumericFormat
              value={value}
              displayType={'text'}
              thousandSeparator={true}
              decimalScale={2}
            />
          </span>
        ),
      },
      {
        Header: 'BP',
        accessor: 'bollard_pull',
        Cell: ({ value }: any) => (
          <span>
            <NumericFormat
              value={value}
              displayType={'text'}
              thousandSeparator={true}
              decimalScale={2}
            />
          </span>
        ),
      },
      {
        Header: 'DECK',
        accessor: 'deck_area',
        Cell: ({ value }: any) => (
          <span>
            <NumericFormat
              value={value}
              displayType={'text'}
              thousandSeparator={true}
              decimalScale={2}
            />
          </span>
        ),
      },
      {
        Header: 'ROV',
        accessor: 'rov',
      },
      {
        Header: 'Current Charterer',
        accessor: 'current_charterer',
      },
      {
        Header: 'Next Charterer',
        accessor: 'next_charterer',
        Cell: ({ row }: any) => (
          <FOSTableNextChartererCell
            currenCharterer={row.original.current_charterer}
            nextCharterer={row.original.next_charterer}
            currentChartererDate={row.original.current_charterer_date}
            nextChartererDate={row.original.next_charterer_date}
          />
        ),
        disableSortBy: true,
      },
      {
        Header: 'Workscope',
        accessor: 'workscope',
        Cell: (cell: any) => (
          <FOSTableTooltipCell value={cell.value}></FOSTableTooltipCell>
        ),
      },
      {
        Header: 'Port Available',
        accessor: 'port',
      },
      {
        Header: 'Comments',
        accessor: 'position_comment',
        Cell: ({ cell, row }: any) => (
          <FOSTableTooltipCell value={cell.value}></FOSTableTooltipCell>
        ),
      },
      {
        Header: 'Date Available',
        accessor: 'date_available',
        Cell: (cell: any) => (
          <FOSTableAvailabilityDateCell
            value={cell.value}
          ></FOSTableAvailabilityDateCell>
        ),
      },
      {
        Header: 'Options Until',
        accessor: 'current_charterer_options_until_date',
        Cell: (cell: any) => (
          <FOSTableDateCell value={cell.value}></FOSTableDateCell>
        ),
      },
      {
        Header: 'Updated By',
        accessor: 'updated_by',
        Cell: ({ row }: any) => {
          // Priority logic: Manual > Current Charterer > Next Charterer > Last Completed > Vessel
          const updatedBy = row.original.manual_availability_updated_by ||
                           row.original.current_charterer_updated_by || 
                           row.original.next_charterer_updated_by ||
                           row.original.last_completed_fixture_updated_by ||
                           row.original.updated_by;
          
          // Extract just the username part (remove any extra formatting)
          const cleanUsername = updatedBy ? updatedBy.toString().split('(')[0].trim() : '';
          
          return <span>{cleanUsername}</span>;
        },
      },
    ],
    []
  );

  const columns_psv = React.useMemo(
    () => [
      {
        Header: 'Vessel Name',
        accessor: 'vessel_name',
        Cell: ({ row }: any) => (
          <FOSTableVesselLinkCell
            imo={row.original.imo}
            vesselName={row.original.vessel_name}
          ></FOSTableVesselLinkCell>
        ),
      },
      {
        Header: 'BHP',
        accessor: 'bhp',
        Cell: ({ value }: any) => (
          <span>
            <NumericFormat
              value={value}
              displayType={'text'}
              thousandSeparator={true}
              decimalScale={2}
            />
          </span>
        ),
      },
      {
        Header: 'DWT',
        accessor: 'dwt',
        Cell: ({ value }: any) => (
          <span>
            <NumericFormat
              value={value}
              displayType={'text'}
              thousandSeparator={true}
              decimalScale={2}
            />
          </span>
        ),
      },
      {
        Header: 'DECK',
        accessor: 'deck_area',
        Cell: ({ value }: any) => (
          <span>
            <NumericFormat
              value={value}
              displayType={'text'}
              thousandSeparator={true}
              decimalScale={2}
            />
          </span>
        ),
      },
      {
        Header: 'Current Charterer',
        accessor: 'current_charterer',
        Cell: ({ row }: any) => (
          <FOSTableCurrentChartererCell
            currentCharterer={row.original.current_charterer}
            dateAvailable={row.original.date_available}
          ></FOSTableCurrentChartererCell>
        ),
      },
      {
        Header: 'Next Charterer',
        accessor: 'next_charterer',
        disableSortBy: true,
        Cell: ({ row }: any) => (
          <FOSTableNextChartererCell
            currenCharterer={row.original.current_charterer}
            nextCharterer={row.original.next_charterer}
            currentChartererDate={row.original.current_charterer_date}
            nextChartererDate={row.original.next_charterer_date}
          />
        ),
      },
      {
        Header: 'Workscope',
        accessor: 'workscope',
        Cell: (cell: any) => (
          <FOSTableTooltipCell value={cell.value}></FOSTableTooltipCell>
        ),
      },
      {
        Header: 'Port Available',
        accessor: 'port',
      },
      // position_comment is the relevant fixture for next charterer
      {
        Header: 'Comments',
        accessor: 'position_comment',
        Cell: ({ cell, row }: any) => (
          <FOSTableTooltipCell value={cell.value}></FOSTableTooltipCell>
        ),
      },
      {
        Header: 'Date Available',
        accessor: 'date_available',
        Cell: (cell: any) => (
          <FOSTableAvailabilityDateCell
            value={cell.value}
          ></FOSTableAvailabilityDateCell>
        ),
      },
      {
        Header: 'Options Until',
        accessor: 'current_charterer_options_until_date',
        Cell: (cell: any) => (
          <FOSTableDateCell value={cell.value}></FOSTableDateCell>
        ),
      },
      {
        Header: 'Updated By',
        accessor: 'updated_by',
        Cell: ({ row }: any) => {
          // Priority logic: Manual > Current Charterer > Next Charterer > Last Completed > Vessel
          const updatedBy = row.original.manual_availability_updated_by ||
                           row.original.current_charterer_updated_by || 
                           row.original.next_charterer_updated_by ||
                           row.original.last_completed_fixture_updated_by ||
                           row.original.updated_by;
          
          // Extract just the username part (remove any extra formatting)
          const cleanUsername = updatedBy ? updatedBy.toString().split('(')[0].trim() : '';
          
          return <span>{cleanUsername}</span>;
        },
      },
    ],
    []
  );

  let columns: any = columns_other;

  const [dataLoaded, setDataLoaded] = useState(false);
  const [tab, setTab] = useState<Tab>(Tab.AHTS);
  const [data, setData] = useState<any>([]);

  if (tab == 'psv') {
    columns = columns_psv;
  } else {
    columns = columns_other;
  }

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState({ id: 'date_available', desc: false });
  const [moodalOpen, setModalOpen] = useRecoilState(modalEditorOpen);

  useEffect(() => {
    if (moodalOpen) {
      return;
    }

    getAvailabilities('no', tab, page, perPage, sortBy).then(
      (dataRes) => {
        setData(dataRes.data);
        setTotalPages(dataRes.pagination.lastPage);
        setDataLoaded(true);
      },
      (reason) => {
        console.log('ERROR');
        setDataLoaded(true);
      }
    );
  }, [tab, page, perPage, sortBy, moodalOpen]);

  const resetPage = () => {
    setPage(1);
  };

  useEffect(() => {
    resetPage();
  }, [tab]);

  return (
    <div>
      <div className='north-sea-spot-caption'>Availability - Norway</div>

      <ul className='nav nav-tabs'>
        <li className='nav-item'>
          <a
            className={tab === Tab.AHTS ? 'nav-link active' : 'nav-link'}
            onClick={(e) => {
              setTab(Tab.AHTS);
            }}
            aria-current='page'
            href='#1'
          >
            AHTS
          </a>
        </li>
        <li className='nav-item'>
          <a
            className={tab === Tab.PSV ? 'nav-link active' : 'nav-link'}
            onClick={(e) => {
              setTab(Tab.PSV);
            }}
            href='#2'
          >
            PSV
          </a>
        </li>
        <li className='nav-item'>
          <a
            className={tab === Tab.Standby ? 'nav-link active' : 'nav-link'}
            onClick={(e) => {
              setTab(Tab.Standby);
            }}
            href='#3'
          >
            Standby
          </a>
        </li>
      </ul>

      <div style={{ marginTop: '20px' }}>
        <FOSTable
          columns={columns}
          data={data}
          isHighlightedRow={(row: any) =>
            availabilityDateChecker(row.date_available)
          }
          setPage={setPage}
          setPerPage={setPerPage}
          currentpage={page}
          perPage={perPage}
          totalPage={totalPages}
          initialSortBy={sortBy}
          onChangeSort={setSortBy}
        ></FOSTable>
      </div>
    </div>
  );
};
